import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
    map, filter, concatMap, catchError, of, timer, switchMap, tap, race,
} from 'rxjs';

import { ViewItemService } from '../../../../../product/product-view/services/view-item.service';
import { ZenitEventApiService } from '../../../services/api/zenit-event-api.service';
import { ZenitEventParserService } from '../../../services/parser/zenit-event-parser.service';
import { ZenitEventSuccess, ZenitEventError, ZenitListItemVisible, ImpressionsHitEventAction, ZenitClearViewedProducts } from '../../actions/zenit.actions';

import { getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { LoadCategorySuccess } from '@pages/category';
import { getIsCategory } from '@pages/category/store/category-page.selectors';
import { HeaderEventAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import { ImpressionsHitEvent } from '@shared/analytics/ZENIT/models/events/impressions-hit.model';
import { ZenitEventName } from '@shared/analytics/ZENIT/models/zenit-general.model';

@Injectable()
export class ImpressionsHitEventEffects {
    private readonly EVENT_ITEM_BATCH = 10;

    constructor(
        private readonly actions$: Actions,
        private readonly zenitEventParserService: ZenitEventParserService,
        private readonly zenitEventApiService: ZenitEventApiService,
        private readonly viewItemService: ViewItemService,
        private readonly router: Router,
        private readonly store: Store
    ) { }

    public EventListItemVisible$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ZenitListItemVisible),
            concatLatestFrom((event) =>
                of(this.viewItemService.viewProduct(event.product, false, false, { itemsBatch: this.EVENT_ITEM_BATCH }))),
            filter(([_, products]) => !!products?.length),
            map(([event, products]) => ImpressionsHitEventAction({
                eventFrom: event.eventFrom,
                place: event.place,
                products,
                relatedsSectionId: event.relatedsSectionId,
            }))
        );
    });

    public EventListItemVisibleTimerZenit$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ZenitListItemVisible),
            switchMap((event) => timer(1100).pipe(
                map(() => this.viewItemService.viewProduct(null, true, false, { itemsBatch: this.EVENT_ITEM_BATCH })),
                filter((products) => !!products?.length),
                map((products) => ImpressionsHitEventAction({
                    eventFrom: event.eventFrom,
                    place: event.place,
                    products,
                    relatedsSectionId: event.relatedsSectionId,
                })),
            )),
        );
    });

    /**
     * Efecto que se encarga de limpiar el registro de productos vistos cuando se navega fuera de una categoría
     */
    public EventListItemVisibleExitZenit$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategorySuccess),
            switchMap(() => race([
                this.router.events.pipe(filter((event) => event instanceof NavigationStart)),
                /* Como el apartado de "Mi cuenta" es aún una página legacy no se utiliza el router para navegar y se recarga la página,
                por lo que para detectar que se navega fuera de la categoría en este caso por ahora necesitamos esta línea */
                this.actions$.pipe(ofType(HeaderEventAnalytics), filter((action) => action.section === 'account')),
            ])),
            concatLatestFrom(() => this.store.select(getIsCategory)),
            filter(([, _isCategory]) => _isCategory),
            map(() => ZenitClearViewedProducts())
        );
    });

    public ClearViewedProducts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ZenitClearViewedProducts),
            tap(() => {
                // Limpia el registro de productos vistos
                this.viewItemService.viewProduct(null, true, true);
            }),
        );
    }, { dispatch: false });

    public EventImpressionsHitZenit$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ImpressionsHitEventAction),
            concatLatestFrom(() => this.store.select(getCmsConfigValue('enabled_zenit'))),
            filter(([_, enabledZenit]) => enabledZenit),
            concatLatestFrom(([action]) => this.zenitEventParserService.getEventObject<ImpressionsHitEvent>(
                ZenitEventName.ImpressionsHit,
                action.eventFrom,
                { items: action?.products, relatedsSectionId: action.relatedsSectionId, place: action.place }
            )),
            concatMap(([_, impressionsHitEvent]) => this.zenitEventApiService.postEvent$(impressionsHitEvent, 'impressions.input.v1')
                .pipe(
                    map(() => ZenitEventSuccess<ImpressionsHitEvent>()({ eventData: impressionsHitEvent })),
                    catchError((error) => of(ZenitEventError({ error })))
                )),
        );
    });
}
